// Parse the JSON output of the `salary_benchmark` LLM command. Tolerates:
//   - `[Job xxx] Starting…` / `[ℹ] Model:` orchestration noise (via cleanLLMOutput)
//   - Stray ```json … ``` wrappers despite format=json
//   - Leading <think>…</think> blocks if /no_think wasn't honored
//   - Multiple {…} balanced objects (we take the first one)
//
// Returns null if no parseable JSON object found (caller should treat as
// "benchmark unavailable" and skip the market bar / leverage frame).

import { cleanLLMOutput } from './clean-llm-output';
import type { SalaryBenchmark } from './types';

function extractFirstBalancedObject(s: string): string | null {
  const start = s.indexOf('{');
  if (start < 0) return null;
  let depth = 0;
  let inString = false;
  let escape = false;
  for (let i = start; i < s.length; i++) {
    const c = s[i];
    if (escape) { escape = false; continue; }
    if (inString) {
      if (c === '\\') { escape = true; continue; }
      if (c === '"') inString = false;
      continue;
    }
    if (c === '"') { inString = true; continue; }
    if (c === '{') depth++;
    else if (c === '}') {
      depth--;
      if (depth === 0) return s.slice(start, i + 1);
    }
  }
  return null;
}

export function parseSalaryBenchmark(raw: string): Omit<SalaryBenchmark, 'fetchedAt'> | null {
  if (!raw) return null;
  let s = cleanLLMOutput(raw);
  // Strip ```json fences if present
  s = s.replace(/```(?:json)?\s*/gi, '').replace(/```/g, '');
  // Strip <think>…</think> blocks if model ignored /no_think
  s = s.replace(/<think>[\s\S]*?<\/think>/gi, '').trim();
  const obj = extractFirstBalancedObject(s);
  if (!obj) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(obj);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const p = parsed as Record<string, unknown>;
  const p25 = Number(p.p25);
  const p50 = Number(p.p50);
  const p75 = Number(p.p75);
  // Sanity: must be non-zero numbers in the right order. If the LLM
  // returned 0s or scrambled, give up — better to show "no benchmark"
  // than a misleading bar.
  if (![p25, p50, p75].every((n) => Number.isFinite(n) && n > 0)) return null;
  if (!(p25 <= p50 && p50 <= p75)) return null;
  // Tier the confidence string conservatively
  const confRaw = String(p.confidence ?? 'medium').toLowerCase();
  const confidence: SalaryBenchmark['confidence'] =
    confRaw === 'high' ? 'high' : confRaw === 'low' ? 'low' : 'medium';
  return {
    p25,
    p50,
    p75,
    currency: String(p.currency ?? 'USD'),
    confidence,
    reasoning: String(p.reasoning ?? ''),
    verify_at: Array.isArray(p.verify_at) ? (p.verify_at as unknown[]).map(String) : undefined,
  };
}
